// Admin-only: review user-submitted featured looks.
import { useEffect, useState } from "react";
import { Loader2, Check, X, Sparkles, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSignedImage } from "@/hooks/useSignedImage";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Row {
  id: string;
  user_id: string;
  tryon_id: string | null;
  source_image_path: string;
  permanent_image_path: string | null;
  kind: "vibe" | "look" | "item";
  template_label: string | null;
  caption: string | null;
  status: "pending" | "approved" | "rejected";
  reject_reason: string | null;
  display_order: number;
  created_at: string;
  reviewed_at: string | null;
  is_custom_upload: boolean;
  submission_source: "credits" | "custom_pass" | "featured_pass" | "tryon";
}

const PRIORITY_BUCKETS = [
  { key: "credits", label: "Custom paid (5 cr)", sla: "Review within 12h" },
  { key: "custom_pass", label: "Custom · 14-day pass", sla: "Review within 24h" },
  { key: "featured_pass", label: "3-day streak pass", sla: "Review within 36h" },
  { key: "tryon", label: "Standard try-on", sla: "Best effort" },
] as const;

type BucketKey = (typeof PRIORITY_BUCKETS)[number]["key"];


const Card = ({
  row,
  children,
}: {
  row: Row;
  children: React.ReactNode;
}) => {
  // Pending custom uploads live in `user-photos`; pending try-on submissions in `tryon-results`.
  // Approved (any kind) live in `featured-images`.
  const bucket =
    row.status === "approved" && row.permanent_image_path
      ? "featured-images"
      : row.is_custom_upload
        ? "user-photos"
        : "tryon-results";
  const path = row.status === "approved" ? row.permanent_image_path : row.source_image_path;
  const url = useSignedImage(bucket, path);
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-card">
      <div className="aspect-[3/4] w-full bg-muted">
        {url ? (
          <img src={url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
      </div>
      <div className="space-y-2 p-3 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
            {row.kind}
          </span>
          <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">
            {PRIORITY_BUCKETS.find((b) => b.key === row.submission_source)?.label.split(" ")[0] ?? row.submission_source}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {new Date(row.created_at).toLocaleDateString()}
          </span>
        </div>

        {row.template_label && (
          <p className="line-clamp-1 font-medium">{row.template_label}</p>
        )}
        {row.caption && <p className="line-clamp-2 text-muted-foreground">"{row.caption}"</p>}
        <p className="truncate text-[10px] font-mono text-muted-foreground">
          uid: {row.user_id.slice(0, 8)}…
        </p>
        {row.reject_reason && (
          <p className="text-[11px] text-destructive">Reason: {row.reject_reason}</p>
        )}
        {children}
      </div>
    </div>
  );
};

export default function AdminFeatured() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectFor, setRejectFor] = useState<Row | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const reload = async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("admin-featured", {
      body: { action: "list" },
    });
    setLoading(false);
    if (error || data?.error) {
      toast.error("Couldn't load submissions");
      return;
    }
    setRows((data?.items ?? []) as Row[]);
  };

  useEffect(() => {
    void reload();
  }, []);

  const callAction = async (id: string, body: Record<string, unknown>, successMsg: string) => {
    setBusyId(id);
    const { data, error } = await supabase.functions.invoke("admin-featured", { body });
    setBusyId(null);
    if (error || data?.error) {
      toast.error(data?.error ?? "Action failed");
      return;
    }
    toast.success(successMsg);
    await reload();
  };

  const approve = (id: string) =>
    callAction(id, { action: "approve", id }, "Approved & featured");
  const reject = async () => {
    if (!rejectFor) return;
    const id = rejectFor.id;
    const reason = rejectReason.trim();
    setRejectFor(null);
    setRejectReason("");
    await callAction(id, { action: "reject", id, reason }, "Rejected");
  };
  const unfeature = (id: string) =>
    callAction(id, { action: "unfeature", id }, "Removed from featured");
  const remove = (id: string) => callAction(id, { action: "delete", id }, "Deleted");
  const reorder = (id: string, display_order: number) =>
    callAction(id, { action: "reorder", id, display_order }, "Order updated");

  const pending = rows.filter((r) => r.status === "pending");
  const approved = rows.filter((r) => r.status === "approved");
  const rejected = rows.filter((r) => r.status === "rejected");

  // Sort pending in priority order, then oldest first within bucket.
  const priorityRank = (s: BucketKey) =>
    PRIORITY_BUCKETS.findIndex((b) => b.key === s);
  const pendingByBucket: Record<BucketKey, Row[]> = {
    credits: [], custom_pass: [], featured_pass: [], tryon: [],
  };
  for (const r of pending) {
    const k = (PRIORITY_BUCKETS.find((b) => b.key === r.submission_source)?.key ?? "tryon") as BucketKey;
    pendingByBucket[k].push(r);
  }
  for (const k of Object.keys(pendingByBucket) as BucketKey[]) {
    pendingByBucket[k].sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
  }
  void priorityRank;

  const renderPendingGrid = (items: Row[], emptyText: string) =>
    items.length === 0 ? (
      <p className="text-sm text-muted-foreground">{emptyText}</p>
    ) : (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {items.map((r) => (
          <Card key={r.id} row={r}>
            <div className="flex gap-2 pt-1">
              <Button size="sm" className="flex-1" onClick={() => approve(r.id)} disabled={busyId === r.id}>
                <Check className="mr-1 h-3.5 w-3.5" /> Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="flex-1"
                onClick={() => { setRejectFor(r); setRejectReason(""); }}
                disabled={busyId === r.id}
              >
                <X className="mr-1 h-3.5 w-3.5" /> Reject
              </Button>
            </div>
          </Card>
        ))}
      </div>
    );

  return (
    <section className="space-y-4 p-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight">
            Featured Submissions
          </h1>
          <p className="text-sm text-muted-foreground">
            Review user-submitted looks. Approved items appear on the Style page top strip.
          </p>
        </div>
        <Button variant="outline" onClick={() => void reload()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
        </Button>
      </header>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="approved">Live ({approved.length})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({rejected.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <Tabs defaultValue={PRIORITY_BUCKETS[0].key}>
            <TabsList className="flex flex-wrap">
              {PRIORITY_BUCKETS.map((b) => (
                <TabsTrigger key={b.key} value={b.key}>
                  {b.label} ({pendingByBucket[b.key].length})
                </TabsTrigger>
              ))}
            </TabsList>
            {PRIORITY_BUCKETS.map((b) => (
              <TabsContent key={b.key} value={b.key} className="mt-4 space-y-3">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {b.sla} · oldest first
                </p>
                {renderPendingGrid(pendingByBucket[b.key], `No ${b.label.toLowerCase()} submissions.`)}
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>


        <TabsContent value="approved" className="mt-4">
          {approved.length === 0 ? (
            <p className="text-sm text-muted-foreground">No live featured items.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {approved.map((r) => (
                <Card key={r.id} row={r}>
                  <div className="flex items-center gap-2 pt-1">
                    <Input
                      type="number"
                      defaultValue={r.display_order}
                      className="h-8 w-16"
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (Number.isFinite(v) && v !== r.display_order) reorder(r.id, v);
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => unfeature(r.id)}
                      disabled={busyId === r.id}
                    >
                      Unfeature
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rejected" className="mt-4">
          {rejected.length === 0 ? (
            <p className="text-sm text-muted-foreground">No rejected items.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              {rejected.map((r) => (
                <Card key={r.id} row={r}>
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1 text-destructive"
                      onClick={() => remove(r.id)}
                      disabled={busyId === r.id}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject submission</DialogTitle>
            <DialogDescription>
              Optional note saved with the submission. The user may see this later.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value.slice(0, 280))}
            placeholder="e.g. Image quality too low, not a good fit, etc."
            rows={3}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectFor(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={reject}>
              <X className="mr-1 h-4 w-4" /> Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
